import { useEffect, useState } from 'react';
import { FormInstance } from 'antd';
import { FieldConfig, FieldOption } from '../types';

/**
 * Hook to handle field dependencies
 */
export const useFieldDependencies = (
  form: FormInstance,
  fields: FieldConfig[]
) => {
  const [dependentOptions, setDependentOptions] = useState<
    Record<string, FieldOption[]>
  >({});
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    // Set up watchers for dependent fields
    const fieldsWithDependencies = fields.filter(
      (field) => field.dependsOn || field.dependencies
    );

    if (fieldsWithDependencies.length === 0) return;

    // Subscribe to form value changes
    const unsubscribe = form.getFieldsValue();

    fieldsWithDependencies.forEach((field) => {
      const dependsOnFields = Array.isArray(field.dependsOn)
        ? field.dependsOn
        : field.dependsOn
        ? [field.dependsOn]
        : [];

      // Add dependencies from the dependencies array
      if (field.dependencies) {
        field.dependencies.forEach((dep) => {
          if (!dependsOnFields.includes(dep.field)) {
            dependsOnFields.push(dep.field);
          }
        });
      }

      dependsOnFields.forEach((dependentFieldName) => {
        // Listen to changes in dependent field
        const currentValue = form.getFieldValue(dependentFieldName);
        handleDependencyChange(field, dependentFieldName, currentValue);
      });
    });
  }, [fields, form]);

  const handleDependencyChange = async (
    field: FieldConfig,
    dependentFieldName: string,
    dependentValue: any
  ) => {
    const allValues = form.getFieldsValue();

    // Find the dependency configuration
    const dependency = field.dependencies?.find(
      (dep) => dep.field === dependentFieldName
    );

    if (!dependency) return;

    // Check condition
    if (dependency.condition && !dependency.condition(dependentValue)) {
      // Reset field value if condition is not met
      form.setFieldValue(field.name, undefined);
      return;
    }

    // Get options if getOptions is provided
    if (dependency.getOptions) {
      setLoadingFields((prev) => ({ ...prev, [field.name]: true }));

      try {
        const options = await Promise.resolve(
          dependency.getOptions(dependentValue, allValues)
        );
        setDependentOptions((prev) => ({
          ...prev,
          [field.name]: options,
        }));

        // Reset field value when options change
        const currentValue = form.getFieldValue(field.name);
        const isValidValue = options.some(
          (opt) => opt.value === currentValue
        );
        if (!isValidValue) {
          form.setFieldValue(field.name, undefined);
        }
      } catch (error) {
        console.error('Error loading dependent options:', error);
        setDependentOptions((prev) => ({ ...prev, [field.name]: [] }));
      } finally {
        setLoadingFields((prev) => ({ ...prev, [field.name]: false }));
      }
    }

    // Handle onDependentChange
    if (dependency.onDependentChange) {
      const currentValue = form.getFieldValue(field.name);
      const newValue = dependency.onDependentChange(
        dependentValue,
        currentValue,
        allValues
      );
      if (newValue !== currentValue) {
        form.setFieldValue(field.name, newValue);
      }
    }
  };

  return {
    dependentOptions,
    loadingFields,
    handleDependencyChange,
  };
};
